/**
 * HUB-D — Onglet Caisse du Centre Activité.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import useActiviteCaisse from '../../../hooks/activite/useActiviteCaisse';
import useActiviteCaisseActions from '../../../hooks/activite/useActiviteCaisseActions';
import ActiviteCaisseAccessBanner from '../ActiviteCaisseAccessBanner';
import ActiviteCaisseKpiStrip from '../ActiviteCaisseKpiStrip';
import ActiviteCaisseSectionTabs from '../ActiviteCaisseSectionTabs';
import ActiviteCaisseSessionCard from '../ActiviteCaisseSessionCard';
import ActiviteCaisseMonthlyCard from '../ActiviteCaisseMonthlyCard';
import ActiviteCaisseMonthlyControls from '../ActiviteCaisseMonthlyControls';
import { ACTIVITE_TAB_IDS } from '../../../utils/activite/activityTabs';
import { CAISSE_SECTIONS } from '../../../utils/caisse/caisseDisplay';

export default function ActiviteCaisseTab({ filters }) {
  const { api, isAdmin } = useAuth();
  const [activeSection, setActiveSection] = useState(CAISSE_SECTIONS.JOURNALIER);
  const [showAllSessions, setShowAllSessions] = useState(false);

  const {
    sessions,
    monthlyRows,
    openStatus,
    kpis,
    loading,
    monthlyLoading,
    error,
    monthlyMonth,
    setMonthlyMonth,
    refresh,
    refreshMonthly,
  } = useActiviteCaisse(api, filters);

  const onRefresh = useCallback(async () => {
    await refresh();
    await refreshMonthly(monthlyMonth);
  }, [refresh, refreshMonthly, monthlyMonth]);

  const actions = useActiviteCaisseActions({ api, onRefresh });

  const visibleSessions = useMemo(
    () => (showAllSessions ? sessions : sessions.slice(0, 10)),
    [sessions, showAllSessions],
  );

  const sectionCounts = useMemo(() => ({
    [CAISSE_SECTIONS.JOURNALIER]: sessions.length,
    [CAISSE_SECTIONS.MENSUEL]: monthlyRows.length,
  }), [sessions, monthlyRows]);

  const handleMonthChange = useCallback((month) => {
    setMonthlyMonth(month);
  }, [setMonthlyMonth]);

  const handleExport = useCallback(() => {
    actions.exportMonthly(monthlyMonth, filters.magasin_id || undefined);
  }, [actions, monthlyMonth, filters.magasin_id]);

  const renderJournalier = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-12" data-testid="activite-caisse-loading">
          <div className="loading-spinner" />
        </div>
      );
    }
    if (!sessions.length) {
      return (
        <div className="text-center py-12 text-[var(--rm-gray-500)]" data-testid="activite-caisse-sessions-empty">
          Aucune session pour cette période
        </div>
      );
    }
    return (
      <div className="space-y-3" data-testid="activite-caisse-sessions-list">
        {visibleSessions.map((caisse) => (
          <ActiviteCaisseSessionCard
            key={caisse.id}
            caisse={caisse}
            actions={actions}
            isAdmin={isAdmin}
            forceClosingId={actions.forceClosingId}
          />
        ))}
        {sessions.length > 10 && (
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={() => setShowAllSessions((v) => !v)}
              className="btn-secondary px-6 py-2.5 min-h-[44px] text-sm"
              data-testid="activite-caisse-sessions-toggle"
            >
              {showAllSessions ? 'Voir moins' : `Voir plus (${sessions.length - 10})`}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderMensuel = () => (
    <div className="space-y-4">
      <ActiviteCaisseMonthlyControls
        month={monthlyMonth}
        onMonthChange={handleMonthChange}
        onRefresh={() => refreshMonthly(monthlyMonth)}
        onExport={handleExport}
        loading={monthlyLoading}
      />
      {monthlyLoading ? (
        <div className="flex justify-center py-12" data-testid="activite-caisse-monthly-loading">
          <div className="loading-spinner" />
        </div>
      ) : !monthlyRows.length ? (
        <div className="text-center py-12 text-[var(--rm-gray-500)]" data-testid="activite-caisse-monthly-empty">
          Aucune caisse validée ce mois
        </div>
      ) : (
        <div className="space-y-3" data-testid="activite-caisse-monthly-list">
          {monthlyRows.map((row) => (
            <ActiviteCaisseMonthlyCard key={row.id} row={row} actions={actions} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      className="rounded-xl border p-4 sm:p-5 space-y-4"
      style={{ background: 'var(--rm-bg-elevated)', borderColor: 'var(--rm-border-subtle)' }}
      data-testid="activite-caisse-tab"
    >
      <ActiviteCaisseAccessBanner filters={filters} />

      <ActiviteCaisseKpiStrip kpis={kpis} openStatus={openStatus} />

      <ActiviteCaisseSectionTabs
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        counts={sectionCounts}
      />

      {error && (
        <div
          className="rounded-xl border p-4 text-sm text-[var(--rm-red-light)]"
          style={{ borderColor: 'var(--rm-border-strong)', background: 'var(--rm-red-muted)' }}
          data-testid="activite-caisse-error"
        >
          {error}
        </div>
      )}

      {activeSection === CAISSE_SECTIONS.JOURNALIER ? renderJournalier() : renderMensuel()}

      <span className="sr-only" data-testid="activite-caisse-tab-id">{ACTIVITE_TAB_IDS.CAISSE}</span>
    </div>
  );
}
